import { queryOptions, useQuery } from "@tanstack/react-query"
import { useTranslation } from "react-i18next"
import { api, type ListResponse } from "@/api/client"
import type { ApiToken } from "@/api/generated/ApiToken"
import type { CreatedApiToken } from "@/api/generated/CreatedApiToken"
import { useApiMutation } from "@/api/mutation"

export const API_TOKEN_SCOPES = ["management", "chat", "workplace", "tasks", "kb:search", "*"] as const
export type ApiTokenScope = (typeof API_TOKEN_SCOPES)[number]

export const apiTokenKeys = {
  all: ["api-tokens"] as const,
  list: () => [...apiTokenKeys.all, "list"] as const,
}

export const apiTokensQuery = () =>
  queryOptions({
    queryKey: apiTokenKeys.list(),
    queryFn: ({ signal }) => api.get<ListResponse<ApiToken>>("/api-tokens", { signal }),
    select: (res) => res.items,
  })

export const useApiTokens = () => useQuery(apiTokensQuery())

export interface ApiTokenCreate {
  name: string
  /** `mcp` (alone) creates a personal token for the `/mcp` endpoint. */
  scopes: (ApiTokenScope | "mcp")[]
  service?: boolean
  expires_in_days?: number
}

export function useCreateApiToken() {
  const { t } = useTranslation()
  return useApiMutation<CreatedApiToken, ApiTokenCreate>({
    mutationFn: (body) => api.post("/api-tokens", body),
    invalidates: [apiTokenKeys.all],
    successToast: () => t("apiTokens.created"),
    errorToast: "unlessFields",
  })
}

export function useRevokeApiToken() {
  const { t } = useTranslation()
  return useApiMutation<void, ApiToken>({
    mutationFn: (token) => api.delete(`/api-tokens/${token.id}`),
    invalidates: [apiTokenKeys.all],
    successToast: (_d, token) => t("apiTokens.revoked", { name: token.name }),
  })
}
